<script setup lang="ts">
import FinestButton from '@/components/FinestButton.vue'
import FinestCard from '@/components/FinestCard.vue'
import EstateObjectsController from '@controllers/Investors/EstateObjectsController'
import FinancingRequestWizardController from '@controllers/Investors/FinancingRequestWizardController'
import { useI18n } from 'vue-i18n'

defineProps<{
  estateObjectId?: string
  title: string
  forFinancingRequest?: boolean
}>()

const financingRequestHref = FinancingRequestWizardController.create({ query: { origin: 'portfolio' } })

const { t } = useI18n({ useScope: 'local' })
</script>

<template>
  <FinestCard :title="t('title')" icon="pi pi-check-circle">
    <div class="flex flex-col items-center gap-4 py-6 text-center">
      <span class="bg-success-subtle text-success flex size-16 items-center justify-center rounded-full">
        <i class="pi pi-check text-3xl!" aria-hidden="true" />
      </span>
      <h2 class="text-2xl font-bold tracking-tight">{{ t('heading') }}</h2>
      <p class="text-ink-2 max-w-prose">
        {{ forFinancingRequest ? t('financingRequestDescription', { title }) : t('description', { title }) }}
      </p>

      <div class="flex flex-wrap items-center justify-center gap-3">
        <Link v-if="forFinancingRequest" :href="financingRequestHref">
          <FinestButton
            variant="gradient"
            size="md"
            :label="t('financingRequestAction')"
            icon="pi pi-arrow-right"
            icon-pos="right" />
        </Link>

        <Link v-if="estateObjectId" :href="EstateObjectsController.show(estateObjectId)">
          <FinestButton
            :variant="forFinancingRequest ? 'secondary' : 'gradient'"
            size="md"
            :label="t('action')"
            icon="pi pi-arrow-right"
            icon-pos="right" />
        </Link>
      </div>
    </div>
  </FinestCard>
</template>

<i18n lang="json">
{
  "de": {
    "title": "Fertig",
    "heading": "Grunddaten eingereicht",
    "description": "„{title}“ liegt jetzt in deinem Portfolio. In der Detailansicht ergänzt du Kaufpreis, Finanzierung und Mieten.",
    "financingRequestDescription": "„{title}“ liegt jetzt in deinem Portfolio. Wähle es in deiner Finanzierungsanfrage aus.",
    "action": "Zur Detailansicht",
    "financingRequestAction": "Zurück zur Finanzierungsanfrage"
  },
  "en": {
    "title": "Done",
    "heading": "Basic data submitted",
    "description": "“{title}” is now part of your portfolio. In the detail view you add purchase price, financing and rents.",
    "financingRequestDescription": "“{title}” is now part of your portfolio. Select it in your financing request.",
    "action": "To the detail view",
    "financingRequestAction": "Back to the financing request"
  }
}
</i18n>
